import { PillarStat } from '../_components';

export function HowModelThinks() {
  return (
    <>
      <h2 className="text-[var(--gold)] text-[18px] font-semibold mb-3 mt-8">
        How the model thinks
      </h2>
      <p className="text-[13px] text-[#cbd5e1] leading-relaxed max-w-4xl mb-4">
        The Visualizer scores every name on two independent axes. The Quality axis
        blends 40 fundamental factors that measure margins, returns on capital,
        growth trajectory, balance sheet strength, and shareholder payout. The
        Valuation axis blends 14 factors, eleven peer-relative multiples plus three
        universe-scaled absolute factors covering asymmetry, discounted cash flow,
        and today&rsquo;s discount to fair value. Roughly two thirds of the inputs
        on each axis are forward-looking estimates rather than trailing data, which
        is the design choice that lets the model anticipate rather than react.
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <PillarStat label="Universe" value="1,099" sub="US, Canadian, global ADR" />
        <PillarStat label="Quality factors" value="40" sub="Across four pillars" />
        <PillarStat label="Valuation factors" value="14" sub="11 peer-relative + 3 absolute" />
        <PillarStat label="Forward / trailing" value="66 / 34" sub="Anticipate, not react" />
      </div>
      <div className="border border-[#1e293b] rounded-lg bg-[#0f1623] p-5 mb-4">
        <h3 className="text-[12px] uppercase tracking-wider text-[var(--gold)] font-semibold mb-2">The composite formula</h3>
        <p className="text-[12px] text-[#cbd5e1] leading-relaxed mb-3">
          The two axes resolve into a single composite score per stock per day
          through a deliberately asymmetric formula. When valuation is favourable,
          quality and cheapness compound geometrically and a great company at a
          reasonable price is rewarded quadratically. When valuation is
          unfavourable, the same stock is penalised linearly. This shape encodes
          the philosophy of the strategy: the largest opportunities sit where
          excellence and value reinforce each other, and the largest mistakes sit
          where price has run ahead of fundamentals. The formula is smooth,
          monotonic, and ranges from roughly minus six to plus seven.
        </p>
        <p className="text-[12px] text-[#cbd5e1] leading-relaxed">
          Per-position upside and downside math anchors on each company&rsquo;s
          own five year price-earnings history, with a stress floor below the
          empirical low and a capped multiple above the empirical high. This
          replaced an earlier approach that pulled from universe-wide percentiles
          and produced results that did not match how individual companies
          actually trade. The current method gives downside and upside numbers a
          portfolio manager can act on rather than statistical artefacts that
          need translation.
        </p>
      </div>
      <div className="border-l-4 border-[var(--gold)] bg-[var(--gold)]/5 p-4 mb-4 max-w-4xl">
        <p className="text-[13px] text-[#e2e8f0] italic leading-relaxed">
          Across twelve annual vintages from 2014 through 2025, the Alpha Zone
          outperformed the rest of the scored universe by an average of 7.7
          percentage points per year, with positive performance in nine of those
          twelve years. Production rules deliver a survivorship-corrected Sharpe
          ratio of 1.31 at 15.7% annual volatility, validated against the full
          Russell 3000.
        </p>
      </div>
      <p className="text-[13px] text-[#cbd5e1] leading-relaxed max-w-4xl mb-6">
        Every stock in the universe lives as a sphere in a three dimensional scene
        where Quality is one axis, Valuation is another, and the Z axis shows a
        momentum signal. The Alpha Zone appears as a translucent ellipsoid around
        the names that currently qualify. A monthly history slider lets the
        manager rewind the entire universe to any past month and watch how the
        spheres move. The same scene exists on the public website, the investor
        deck, the portfolio page, and the alpha zone page, scoped to the relevant
        subset on each surface. It is not a chart of the model. It is the model.
      </p>
    </>
  );
}
